// File: `typescript/apps/front-end/src/main/react/series/SeriesButtons.tsx`
import React, {useMemo, useState} from "react";
import {AjaxSeriesRestControllerApi, SeriesDTO} from "@/generated-client";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import {ClientFactory} from "@/main/common/ClientFactory";
import {CtaButton, CtaButtonDropdown, CtaToggle, type DropdownSection} from "@/main/react/buttons/CtaButton";
import {Bookmark, BookOpen, ChartColumn, Flag, MoreHorizontal, PlusSquare, Settings, Share} from "lucide-react";
import {shareUrl} from "@/main/util/PlatformIdentifierUtil";
import {navigateToUrl} from "@/main/common/BrowserNavigation";
import {Localizer} from "@/main/react/Types";
import {showAddDiscussionsToSeriesDialog} from "@/main/react/Dialog/SeriesAddDiscussionToSeriesDialog";
import {showUpdateSeriesSettingsDialog} from "@/main/react/Dialog/UpdateSeriesSettingsDialog";
import { emitSeriesFollowAdded, emitSeriesFollowRemoved, onSeriesFollowAdded, onSeriesFollowRemoved } from "@/main/common/EventRegistry";
import { MetaHandler } from "@/main/common/MetaHandler";
import { useViewerRelationshipState } from "@/main/common/ViewerActionRecords";
import {cn} from "@/lib/utils";
import logger from "@/lib/logger";
import {queueSeriesContinue, queueSeriesStart, trackFollowAdded, trackFollowRemoved, trackFollowStarted, trackShare, type AnalyticsSurface} from "@/main/analytics/analyticsEvents";
import {toast} from "sonner";
import {ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface} from "@/main/react/account_access/AccountAccessDialogContext";
// Lazy on purpose: this component renders on every series card, and the dialog drags the attachment
// picker with it. The chunk loads on the first press, never on the page (rule 27).
import {showReportEntityDialog} from "@/main/react/Dialog/ReportEntityDialogLazy";
import { buildSeriesDiscussionHref, type SeriesReadingState } from "@/main/react/series/SeriesReadingTypes";
import { buildSeriesFollowContinuationUrl, consumeSeriesFollowIntent } from "@/main/react/account_access/seriesFollowIntent";

type SeriesButtonsProps = {
    seriesPublicId: string;
    seriesSlug: string;
    initiallyOwner?: boolean;
    initiallyFollowing?: boolean;
    seriesTitle?: string;
    seriesDescription?: string;
    seriesThumbnailUrl?: string;
    mainLocale?: string;
    privateAccess?: boolean;
    bookmarksCollection?: boolean;
    sensitive?: boolean;
    seriesReadingState?: SeriesReadingState;
    /**
     * How many posts the series holds, when the caller knows (the series page header does). Round 38 D8
     * (`SERIES-36`): it decides which owner action is the one filled primary and whether Insights is
     * offered at all. Absent (cards), the reading state stands in for it.
     */
    discussionsCount?: number | null;
    localizer: Localizer;
    allowWrap?: boolean;
    /**
     * Collapse the SECONDARY actions (share, edit) to icon-only below `sm`, keeping the primary
     * labelled (`DASHBOARD-36`).
     *
     * <p>At 412px the English row (Add post / Share / Edit) fits with about 11 CSS px of slack. A
     * labelled `sm` pill is roughly `10 + 18 + 6 + textWidth + 10`, and at 12px text ≈ 6.2px per
     * character "Agregar publicación" is ~68px wider than "Add post" — so in Spanish the row overflows
     * and "Editar" wraps to a second right-aligned line on EVERY card. Whatever is chosen has to hold
     * for the longest locale, not just the captured one, so the fix is on the two actions whose icons
     * already carry their meaning rather than on a shorter Spanish string.
     *
     * <p>Opt-in rather than derived, for the same reason `alignStart` and `stretchPrimary` are props on
     * `GroupButtons`: this component also renders the series page's own header row, where the labels
     * are the page's actions and belong on screen. Only `SeriesCardDisplayer` passes it.
     *
     * <p>Prior art and the budget this applies: `designs/01-now/2026-07-29-mobile-avatar-load-resilience-and-card-action-row-fit-design.md`.
     */
    compactLabels?: boolean;
    /**
     * Render the owner-only "Series insights" entry point (`/series/{slug}/metrics`).
     *
     * <p>Opt-in per call site, for the same reason `compactLabels` is: only the series PAGE header
     * has room for a fourth owner control. A series card's action row already fits its three with
     * about 11 CSS px of slack at 412px in English and overflows in Spanish, so deriving this from
     * `initiallyOwner` would silently wrap every owned card in the dashboard list.
     *
     * <p>Never shown for the automatic bookmarks collection: it is a private reading list, not a
     * published thing whose readership is worth measuring.
     */
    showMetricsEntry?: boolean;
    displayMode?: "full" | "primary-only";
    /**
     * `SERIES-59`: the host owns the row's alignment. Without it the root claims the right edge
     * (`ml-auto justify-end`), which is right for a card footer and wrong under a centred message.
     */
    hostAligned?: boolean;
    /**
     * U43 (`SERIES-12`, `-36`, `-63`): the series PAGE header's arrangement. The primary starts at the
     * content edge on a phone; Share is icon-only below `sm`; the owner's management actions (Insights,
     * Edit) live in the `…` overflow; and the secondary controls wrap as ONE set, so the `…` can never be
     * stranded alone on a line. Only `SeriesHeaderDisplayer` passes it.
     */
    headerLayout?: boolean;
    /** Where this button is mounted, for follow/unfollow + reading attribution. See the join/follow analytics design. */
    surface?: AnalyticsSurface;
};

type SeriesFollowApi = AjaxSeriesRestControllerApi & {
    followSeries(seriesPublicId: string, options?: unknown): Promise<unknown>;
    unfollowSeries(seriesPublicId: string, options?: unknown): Promise<unknown>;
};

export default function SeriesButtons(props: SeriesButtonsProps) {
    const {
        seriesPublicId,
        initiallyOwner = false,
        initiallyFollowing = false,
        seriesTitle = "",
        seriesDescription = "",
        seriesThumbnailUrl = "",
        mainLocale,
        privateAccess = false,
        bookmarksCollection = false,
        sensitive = false,
        seriesReadingState,
        discussionsCount,
        seriesSlug,
        localizer,
        allowWrap = true,
        compactLabels = false,
        showMetricsEntry = false,
        displayMode = "full",
        hostAligned = false,
        headerLayout = false,
        surface = "page",
    } = props;

    const seriesApi: SeriesFollowApi = useMemo(() => ClientFactory.getAjaxSeriesRestControllerApi() as SeriesFollowApi, []);
    // Not plain useState: this component unmounts when a collapsible section closes (the home
    // discovery rail), and the `initiallyFollowing` prop it would re-seed from is a stale page
    // bootstrap value. See ViewerActionRecords.
    const [isFollowing, setIsFollowing] = useViewerRelationshipState("series-follow", seriesPublicId, initiallyFollowing);
    const [busy, setBusy] = useState(false);
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const openAddDiscussionsToSeriesDialog = showAddDiscussionsToSeriesDialog as (seriesPublicId: string, localizer: Localizer, seriesSummary?: SeriesDTO) => void;
    /*
     * Round 38 D7 (`SERIES-23`): the series gate, not the people-follow one. It names the series and says
     * what following it gets, and it carries `?follow=1` back to the series' own page, where the header's
     * Follow completes the follow once (`seriesFollowIntent.ts`, the `?join=1` model). A series with no
     * slug (a partial card payload) keeps the gate without the continuation rather than a broken link.
     */
    const followAccountAccessDialog = {
        ...ACCOUNT_ACCESS_DIALOG_COPY.seriesFollow,
        contextTitleArgs: [seriesTitle],
        surface: "series_follow",
        ...(seriesSlug ? { continuation: { kind: "url" as const, url: buildSeriesFollowContinuationUrl(seriesSlug) } } : {}),
    };
    /*
     * FI-039 / FA-F1 (round-11 slice 1b): the member half of series reporting, whose server half
     * (`reportSeries`) shipped 2026-09-14 with nothing able to reach it. Mirrors `GroupButtons`' report
     * item: an overflow entry, login-gated through the shared report copy, never offered to the owner
     * (an owner can edit or delete their own series; a platform report against it addresses a
     * bystander), never on the private bookmarks collection (nobody else can see it), and never in the
     * `primary-only` rail, which is a follow button and nothing else.
     */
    const reportDropdownSections: DropdownSection[] = displayMode === "full" && !initiallyOwner && !bookmarksCollection && seriesPublicId
        ? [{
            id: "moderation",
            title: null,
            items: [{
                id: "report-series",
                icon: Flag,
                requiresLogin: true,
                accountAccessDialog: withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.report, "series_report"),
                destructive: true,
                text: localizer("report.modal.submit_button.entity", localizer("report.entity.series")),
                onClick: (ev?: Event) => {
                    ev?.preventDefault?.();
                    ev?.stopPropagation?.();
                    showReportEntityDialog(localizer, "series", seriesPublicId);
                },
            }],
        }]
        : [];
    const readingTarget = React.useMemo(() => {
        if (!seriesReadingState || bookmarksCollection) {
            return undefined;
        }
        if (seriesReadingState.completed) {
            return seriesReadingState.firstDiscussion ?? undefined;
        }
        return seriesReadingState.targetDiscussion ?? seriesReadingState.firstDiscussion ?? undefined;
    }, [bookmarksCollection, seriesReadingState]);
    const readingHref = React.useMemo(() => {
        const targetSlugOrPublicId = readingTarget?.discussionSlugOrPublicId?.trim();
        if (!targetSlugOrPublicId || !seriesPublicId) {
            return undefined;
        }
        return buildSeriesDiscussionHref(targetSlugOrPublicId, { contextSeriesPublicId: seriesPublicId });
    }, [readingTarget, seriesPublicId]);
    const readingButtonText = React.useMemo(() => {
        if (!readingHref || !seriesReadingState) {
            return undefined;
        }
        if (seriesReadingState.completed) {
            return localizer("series.reading.start_again");
        }
        const currentPosition = Number(seriesReadingState.currentPosition ?? 0);
        const totalCount = Number(seriesReadingState.totalCount ?? 0);
        if (Boolean(seriesReadingState.resumable) && currentPosition > 0 && totalCount > 0) {
            return localizer("series.reading.continue_with_position", String(currentPosition), String(totalCount));
        }
        return localizer("series.reading.start");
    }, [localizer, readingHref, seriesReadingState]);
    /*
     * Round 38 D8 (`SERIES-36`): the owner gets ONE filled primary. With no post the series has nothing to
     * read, so Add post leads (and the reading CTA is withheld even if a stale reading state names a post);
     * with posts Start reading leads and Add post steps down to outline. Insights waits for the first post:
     * a page of zeros about a series nobody can read yet is not a next step.
     */
    const hasPosts = discussionsCount != null ? Number(discussionsCount) > 0 : Boolean(readingHref);
    const showReadingCta = Boolean(readingHref && readingButtonText) && (!initiallyOwner || hasPosts);
    const addPostVariant = showReadingCta ? "outline" as const : "primary" as const;
    const readingIsContinue = React.useMemo(() => {
        if (!seriesReadingState || seriesReadingState.completed) {
            return false;
        }
        const currentPosition = Number(seriesReadingState.currentPosition ?? 0);
        const totalCount = Number(seriesReadingState.totalCount ?? 0);
        return Boolean(seriesReadingState.resumable) && currentPosition > 0 && totalCount > 0;
    }, [seriesReadingState]);
    // Queued, not immediate: this control renders as a `CtaButton` with an `href`, so the press is a
    // navigation and an immediate `dataLayer` push races the document teardown (review F2, 2026-08-25).
    const onReadingCtaClick = () => {
        if (readingIsContinue) {
            queueSeriesContinue(surface);
        } else {
            queueSeriesStart(surface);
        }
    };
    /**
     * `DISCUSSION-5`, operator decision `A` (2026-08-20, applied wave 8): **filled primary is reserved
     * for Reply.** The rail rendered seven saturated `--primary` pills (three Join, four Follow) down a
     * single column while the page's own primary action sat below the fold, so the loudest, most
     * repeated element on a discussion was about content other than the article being read.
     *
     * `primary-only` therefore draws `outline`, not `primary`. The variant decision has to live here
     * rather than in `DiscussionRail`, which cannot style a button it does not own — and it has to move
     * in the same change as `GroupButtons`, or an outline Follow lands beside a filled Join inside one
     * rail card. Six non-test call sites pass `displayMode="primary-only"`, the first-run
     * `WelcomeOverlay` among them; the operator widened the scope to include it deliberately.
     */
    // U43 (`SERIES-63`): Follow is second in the header's order of importance (Start reading, Follow,
    // Share, More), so it never rests weaker than Share's outline. Outline in every mode and state.
    const followVariant = "outline" as const;

    React.useEffect(() => {
        if (!seriesPublicId || !loggedInUsername) {
            return;
        }

        const unsubscribeAdded = onSeriesFollowAdded((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId || payload.followerUsername !== loggedInUsername) {
                return;
            }
            setIsFollowing(true);
        });

        const unsubscribeRemoved = onSeriesFollowRemoved((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId || payload.followerUsername !== loggedInUsername) {
                return;
            }
            setIsFollowing(false);
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [loggedInUsername, seriesPublicId]);

    const onAddDiscussionToSeries: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        openAddDiscussionsToSeriesDialog(seriesPublicId, localizer, {
            publicId: seriesPublicId,
            slug: seriesSlug,
            title: seriesTitle,
            description: seriesDescription,
            thumbnailUrl: seriesThumbnailUrl,
            mainLocale,
            privateAccess,
            bookmarksCollection,
            sensitive,
            isViewerOwner: initiallyOwner,
            isViewerFollowing: isFollowing,
        });
    };

    const toggleFollow = async (followSurface: AnalyticsSurface = surface) => {
        if (busy || initiallyOwner || !seriesPublicId) return;

        setBusy(true);
        const previous = isFollowing;
        setIsFollowing(!previous);

        try {
            if (previous) {
                await seriesApi.unfollowSeries(seriesPublicId, {params: {surface: followSurface}});
                if (loggedInUsername) {
                    emitSeriesFollowRemoved({
                        seriesPublicId,
                        followerUsername: loggedInUsername,
                    });
                }
                trackFollowRemoved("series", followSurface);
                toast.success(localizer("typescript.series.unfollow.success"));
            } else {
                // Intent event fires before the mutation so click->success conversion is measurable.
                trackFollowStarted("series", followSurface);
                await seriesApi.followSeries(seriesPublicId, {params: {surface: followSurface}});
                if (loggedInUsername) {
                    emitSeriesFollowAdded({
                        seriesPublicId,
                        followerUsername: loggedInUsername,
                        follower: {
                            username: loggedInUsername,
                        },
                    });
                }
                trackFollowAdded("series", followSurface);
                toast.success(localizer("typescript.series.follow.success"));
            }
        } catch (err) {
            setIsFollowing(previous);
            if (isHandledAuthSessionError(err)) {
                return;
            }
            logger.error("Follow/unfollow series failed", {err, seriesPublicId});
            toast.error(localizer("typescript.series.follow.update_failed"));
        } finally {
            setBusy(false);
        }
    };

    // Round 38 D7: resume a follow the visitor asked for before signing in. Only the series PAGE header
    // consumes the intent (a card or the list's own Follow on the same page must not race it), only for a
    // signed-in non-owner, and once: the param is stripped whether or not a follow was needed.
    const followIntentHandledRef = React.useRef(false);
    React.useEffect(() => {
        if (followIntentHandledRef.current || surface !== "series_header" || !MetaHandler.isUserLoggedIn()) {
            return;
        }
        if (!consumeSeriesFollowIntent(seriesSlug)) {
            return;
        }
        followIntentHandledRef.current = true;
        if (initiallyOwner || isFollowing || bookmarksCollection) {
            return;
        }
        void toggleFollow("deep_link_resume");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openSettings = () => {
        showUpdateSeriesSettingsDialog({
            seriesPublicId,
            title: seriesTitle,
            description: seriesDescription,
            thumbnailUrl: seriesThumbnailUrl,
            mainLocale,
            privateAccess,
            bookmarksCollection,
            sensitive,
            localizer,
        });
    };
    const openMetrics = () => {
        navigateToUrl(`/series/${seriesSlug}/metrics`, {source: "series-metrics-entry"});
    };
    const showMetrics = displayMode !== "primary-only" && showMetricsEntry && initiallyOwner && !bookmarksCollection && hasPosts;
    const showOwnerEdit = displayMode !== "primary-only" && initiallyOwner && !bookmarksCollection;
    // U43 (`SERIES-36`): on the page header the owner's management actions are one overflow, not two more
    // pills in a row that already holds the primary, Add post and Share.
    const ownerOverflowSections: DropdownSection[] = headerLayout && (showMetrics || showOwnerEdit)
        ? [{
            id: "owner",
            title: null,
            items: [
                ...(showMetrics ? [{
                    id: "series-metrics",
                    icon: ChartColumn,
                    requiresLogin: false as const,
                    text: localizer("series.metrics.title"),
                    onClick: () => openMetrics(),
                }] : []),
                ...(showOwnerEdit ? [{
                    id: "series-edit",
                    icon: Settings,
                    requiresLogin: false as const,
                    text: localizer("series.edit"),
                    onClick: () => openSettings(),
                }] : []),
            ],
        }]
        : [];
    const overflowSections = [...ownerOverflowSections, ...reportDropdownSections];

    // SERIES-75 (V63 b): the header's pills were 32px `text-xs` while the list toolbar 80px below is 36px
    // `text-sm` (`SimpleSelect` `h-9`), so the page's main actions read as secondary to the list tools.
    // From `sm` up the header matches the toolbar; below `sm` the coarse-pointer 44px floor already
    // evens both rows, and cards keep the dense pill.
    const headerPillSizeClass = headerLayout ? "sm:h-9 sm:text-sm" : "";

    const readingCta = displayMode !== "primary-only" && showReadingCta ? (
        <CtaButton
            icon={BookOpen}
            text={readingButtonText ?? ""}
            href={readingHref}
            onClick={onReadingCtaClick}
            size={"sm"}
            variant="primary"
            extraClass={cn("series-reading-button", headerPillSizeClass)}
            playwrightId="series-reading-button"
        />
    ) : null;

    const secondaryControls = (
        <>
            {!initiallyOwner && (
                <CtaToggle
                    icon={Bookmark}
                    text={isFollowing ? localizer("series.unfollow") : localizer("series.follow")}
                    buttonActive={isFollowing}
                    extraClass={cn("series-follow-button", headerPillSizeClass)}
                    size={"sm"}
                    variant={followVariant}
                    requiresLogin
                    accountAccessDialog={followAccountAccessDialog}
                    onPressedChange={() => void toggleFollow()}
                    disabled={busy}
                />
            )}

            {displayMode !== "primary-only" && initiallyOwner && !bookmarksCollection && (
                <CtaButton
                    icon={PlusSquare}
                    text={localizer("series.add.discussion")}
                    onClick={onAddDiscussionToSeries}
                    disabled={busy}
                    size={"sm"}
                    variant={addPostVariant}
                    extraClass={cn("series-add-discussion-button", headerPillSizeClass)}
                    playwrightId="series-add-discussion-button"
                />
            )}

            {displayMode !== "primary-only" && !bookmarksCollection && (
                <CtaButton
                    icon={Share}
                    // `EMPTY-STATES-105` (wave-12 sheet Q36, option A). `outline`, matching the Edit
                    // control immediately beside it. Both collapse to icon-only below the compact
                    // breakpoint, and at that size a `ghost` control has no resting box at all — so the
                    // pair read as one button and one decoration. Resolved by giving Share a resting
                    // affordance rather than by taking Edit's away.
                    variant="outline"
                    size={"sm"}
                    text={localizer("literal.share")}
                    displayTextOnMobile={!compactLabels && !headerLayout}
                    onClick={async () => {
                        trackShare("series", "series_actions");
                        const sharePath = `/series/${seriesSlug}`;
                        const shareFull = (typeof window !== "undefined" ? window.location.origin : "") + sharePath;
                        await shareUrl(shareFull, localizer("series.share.title"), localizer,
                            { entityType: "SERIES", entityId: seriesPublicId, entityTitle: seriesTitle });
                    }}
                    disabled={busy}
                    extraClass={cn("series-share-button", headerPillSizeClass)}
                />
            )}

            {!headerLayout && showMetrics && (
                <CtaButton
                    icon={ChartColumn}
                    text={localizer("series.metrics.title")}
                    displayTextOnMobile={!compactLabels}
                    onClick={openMetrics}
                    disabled={busy}
                    size={"sm"}
                    variant="outline"
                    extraClass="series-metrics-button"
                    playwrightId="series-metrics-button"
                />
            )}

            {!headerLayout && showOwnerEdit && (
                <CtaButton
                    icon={Settings}
                    text={localizer("series.edit")}
                    displayTextOnMobile={!compactLabels}
                    onClick={openSettings}
                    disabled={busy}
                    size={"sm"}
                    variant="outline"
                    extraClass="series-edit-button"
                    playwrightId="series-edit-button"
                />
            )}

            {overflowSections.length > 0 ? (
                <CtaButtonDropdown
                    icon={MoreHorizontal}
                    // The same existing key `GroupButtons`' overflow trigger uses, so this control costs
                    // no new catalog entry (rule 11).
                    ariaLabel={localizer("typescript.discussion.preview.publish_settings.more_options")}
                    playwrightId="series-more-options-button"
                    variant="ghost"
                    size={"sm"}
                    extraClass={cn("series-more-options-button min-w-0", headerPillSizeClass)}
                    dropdownSections={overflowSections}
                />
            ) : null}
        </>
    );

    if (headerLayout) {
        return (
            <div
                className="flex w-full max-w-full flex-wrap items-center justify-start gap-2 sm:ml-auto sm:justify-end"
                data-playwright-id="series-header-actions"
            >
                {readingCta}
                {/* One set: Follow / Add post, Share and the overflow wrap together or not at all. */}
                <div className="flex flex-nowrap items-center gap-2" data-playwright-id="series-header-secondary-actions">
                    {secondaryControls}
                </div>
            </div>
        );
    }

    return (
        <div className={cn(
            "flex max-w-full items-center gap-2",
            hostAligned ? null : "ml-auto justify-end",
            allowWrap ? "flex-wrap" : "flex-nowrap",
        )}>
            {readingCta}
            {secondaryControls}
        </div>
    );
}

